import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { REST_BASE_URL } from '../app.config';
import { ScannedDokument } from '../components/visum-erfassung/dokumente/scanned.document';
import { ValueListItem } from '../models/rest/valuelist/valuelist';
import { AppService } from './app.service';

export interface DocumentSettings {
  abhMaxFileSizeInBytes: number;
  abhMaxNumberOfFiles: number;
  baMaxFileSizeInBytes: number;
  baMaxNumberOfFiles: number;
  baDetectionPattern: string;
}

@Injectable({
  providedIn: 'root'
})
export class DocumentService {
  /**
   * Die Basis URL zum Ansprechen der Document.
   */
  private static readonly baseUrlDocument = REST_BASE_URL + '/document';

  /**
   * Die URL zum Abfragen der Dokument-Einstellungen.
   */
  private static readonly urlDocumentSettings = DocumentService.baseUrlDocument + '/settings';

  private subscriptions: Subscription = new Subscription();
  private settings: DocumentSettings;

  /**
   * Constructor
   *
   * @param httpClient to ping
   * @param appService
   */
  constructor(
    private readonly httpClient: HttpClient,
    private readonly appService: AppService
  ) {
  }

  getSettings(): Observable<DocumentSettings> {
    const ret: BehaviorSubject<DocumentSettings> = new BehaviorSubject<DocumentSettings>(this.getDefaultSettings());

    ret.getValue().abhMaxFileSizeInBytes = this.settings.abhMaxFileSizeInBytes;
    ret.getValue().abhMaxNumberOfFiles = this.settings.abhMaxNumberOfFiles;
    ret.getValue().baMaxFileSizeInBytes = this.settings.baMaxFileSizeInBytes;
    ret.getValue().baMaxNumberOfFiles = this.settings.baMaxNumberOfFiles;
    ret.getValue().baDetectionPattern = this.settings.baDetectionPattern;
    return ret;
  }

  fetchSettings(): Observable<DocumentSettings> {
    return this.httpClient.get<DocumentSettings>(
      DocumentService.urlDocumentSettings
    ).pipe(tap({
      next: (result) => {
        this.settings = result;
      },
      error: error => {
        console.error('Die Dokument-Einstellungen konnten nicht ermittelt werden.');
        console.error(error.error.message);
        this.settings = this.getDefaultSettings();
        console.error('Es werden die Standardwerte verwendet : ' + this.settings);
      }
    }));
  }

  validateDocuments(control: AbstractControl): { [key: string]: boolean } | null {
    const dokumente = control.value as ScannedDokument[];
    const invalidObj = { size: true };
    let valid = this.appService.isCVisum() ? true : dokumente && dokumente.length > 0;
    // Wichtig, dass das als erstes gemacht wird, sobald die Methode zum ersten Mal aufgerufen wird.
    // (Passiert automatisch beim ersten Validieren, wenn die Liste noch leer ist.)
    if (this.settings === undefined) {
      this.subscriptions.add(
        this.fetchSettings().subscribe((settings: DocumentSettings) => {
          this.settings = settings;
        })
      );
    }
    if (dokumente === undefined || dokumente === null || dokumente.length === 0) {
      return null;
    }
    dokumente.forEach(dokument => {
      if (this.isDocumentSizeInvalid(dokument)) {
        valid = false;
      }
    });
    if (valid) {
      return null;
    } else {
      return invalidObj;
    }
  }

  public isDocumentSizeInvalid(dokument: ScannedDokument): boolean {
    const size = dokument.sizeInBytes;
    if (this.isBADocument(dokument)) {
      return size > this.settings.baMaxFileSizeInBytes;
    } else {
      return size > this.settings.abhMaxFileSizeInBytes;
    }
  }

  public isValueListBADocument(item: ValueListItem): boolean {
    return item.fields?.barelevant || item.key.match(this.settings.baDetectionPattern) != null;
  }

  public isBADocument(dokument: ScannedDokument): boolean {
    return dokument.barelevant || dokument.doctype.match(this.settings.baDetectionPattern) != null;
  }

  private getDefaultSettings(): DocumentSettings {
    return {
      abhMaxFileSizeInBytes: 10000000,
      abhMaxNumberOfFiles: 100,
      baMaxFileSizeInBytes: 2000000,
      baMaxNumberOfFiles: 5,
      baDetectionPattern: '^.+BA$'
    } as DocumentSettings;
  }

  public setDefaultSettings(): DocumentSettings {
    const settings = this.getDefaultSettings();
    this.settings = settings;
    return settings;
  }

}
